/**
 * Error Handler Centralizado
 * Tratamento consistente de erros em toda a aplicação
 */


import {
    DomainError,
    ValidationError,
    NotFoundError,
    BusinessRuleError,
    UnauthorizedError,
    InfrastructureError
} from './DomainErrors';

export class ErrorHandler {
    /**
     * Trata erro e notifica usuário de forma apropriada
     */
    static handle(error: Error, context: string): void {
        // Log estruturado
        this.log(error, context);

        // Notifica usuário baseado no tipo de erro
        const userMessage = this.getUserMessage(error);
        this.notifyUser(userMessage, this.getSeverity(error));
    }

    /**
     * Log estruturado para debugging
     */
    private static log(error: Error | any, context: string): void {
        const logData = {
            context,
            name: error?.name,
            message: error?.message,
            stack: error?.stack,
            timestamp: new Date().toISOString()
        };

        if (error instanceof InfrastructureError && error.originalError) {
            console.error(`[${context}] Infrastructure Error:`, logData, {
                originalError: error.originalError.message
            });
        } else if (error instanceof DomainError) {
            console.warn(`[${context}] Domain Error:`, logData);
        } else {
            // Loga o erro completo para debug
            console.error(`[${context}] Unexpected Error:`, logData, error);
        }
    }

    /**
     * Retorna mensagem apropriada para o usuário
     */
    private static getUserMessage(error: Error): string {
        if (error instanceof ValidationError) {
            return error.field
                ? `${error.field}: ${error.message}`
                : error.message;
        }

        if (error instanceof NotFoundError) {
            return error.message;
        }

        if (error instanceof BusinessRuleError) {
            return error.message;
        }

        if (error instanceof UnauthorizedError) {
            return error.message;
        }

        if (error instanceof InfrastructureError) {
            return error.message || 'Erro de conexão. Por favor, tente novamente.';
        }

        if (error instanceof DomainError) {
            return error.message;
        }

        // Erro desconhecido - não expor detalhes técnicos
        return 'Ocorreu um erro inesperado. Por favor, tente novamente.';
    }

    /**
     * Determina severidade do erro
     */
    private static getSeverity(error: Error): 'error' | 'warning' | 'info' {
        if (error instanceof ValidationError) return 'warning';
        if (error instanceof BusinessRuleError) return 'warning';
        if (error instanceof NotFoundError) return 'info';
        return 'error';
    }

    /**
     * Notifica usuário via toast
     */
    private static notifyUser(message: string, severity: 'error' | 'warning' | 'info'): void {
        // Import dinâmico para evitar dependência circular
        import('../../utils/toast').then(({ notify }) => {
            switch (severity) {
                case 'error':
                    notify.error(message);
                    break;
                case 'warning':
                    notify.warning(message);
                    break;
                case 'info':
                    notify.info(message);
                    break;
            }
        }).catch(() => {
            // Fallback para console se import falhar
            console.log(`[Notification] ${severity.toUpperCase()}: ${message}`);
        });
    }

    /**
     * Wrapper para async functions com tratamento de erro
     */
    static async execute<T>(
        fn: () => Promise<T>,
        context: string,
        onError?: (error: Error) => void
    ): Promise<T | null> {
        try {
            return await fn();
        } catch (error) {
            this.handle(error as Error, context);
            if (onError) {
                onError(error as Error);
            }
            return null;
        }
    }
}
